import { useEffect } from 'react';
import { HTTP_CODE_UNAUTHORIZED, useSession } from '@distribusion/portal-lib/auth';
import { ErrorPage, FullScreenSpin } from '@distribusion/portal-lib/components';
import { useSetAmplitudeUser } from 'lib/analytics';
import { useSetSentryUser } from 'lib/sentry';
import { useTranslation } from 'lib/translation';

import {
  JWT_TOKEN_REFRESH_INTERVAL_MS,
  REFETCH_ON_USE_SESSION_ERROR_INTERVAL_MS,
} from './constants';

interface AuthControllerProps {
  children: React.ReactElement;
}

// TODO: could be moved to shared and should be also covered with tests
export const AuthController = ({ children }: AuthControllerProps) => {
  /**
   * This is a root session controller
   *
   * It makes:
   * - check if user is authenticated
   * - refresh jwt token
   * - check authentication on window focus
   *
   * User will be redirected to login when:
   * - any getSession respond with 401
   * - any request that is authenticated will respond with 401, and getSession run after that will also respond with 401
   */
  const { t } = useTranslation();
  const { isLoading, isSuccess, isError, error, refetch } = useSession({
    // Retry will run it into loop on 401
    retry: false,
    // To keep jwt updated
    refetchInterval: JWT_TOKEN_REFRESH_INTERVAL_MS,
    // To check auth on window focus
    refetchOnWindowFocus: true,
  });

  useSetAmplitudeUser();
  useSetSentryUser();

  // Retry `getSession` 5 seconds after error for some cases like Ory "throttle" or "timeout" errors.
  // It should be an external retry to avoid infinite loading (like `useQuery` do).
  // It wont affect flow on 401 error, tested.
  useEffect(() => {
    if (isError) {
      const timer = setInterval(refetch, REFETCH_ON_USE_SESSION_ERROR_INTERVAL_MS);
      return () => clearInterval(timer);
    }
  }, [isError, refetch]);

  if (isLoading) {
    return <FullScreenSpin />;
  }

  if (isError) {
    if (error.response && error.response.status === HTTP_CODE_UNAUTHORIZED) {
      return <FullScreenSpin />;
    }

    return (
      <ErrorPage
        title={t('auth.titles.service_is_under_maintenance')}
        description={t('auth.descriptions.service_is_under_maintenance')}
      />
    );
  }

  if (isSuccess) {
    return children;
  }

  return null;
};
